'use client'

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Logo } from '@/components/logo'
import Image from 'next/image'
import { LandingPageSectionHeader } from '@/features/landing-page/landing-page-section-header'
import { PUBLIC_LANDING_ROUTES } from '@/setup/routing/routing'
import { ALL_SOLUTION_ITEMS } from '@/features/landing-page/helpers/landing-solutions'
import { useLandingAuthContext } from '@/features/landing-page/landing-auth-context'
import { useLandingPageContext } from '@/features/landing-page/landing-page-context'
import { cn } from '@/utils/styles/cn'
import Link from 'next/link'

import { Button } from '@/components/ui/button'
import { LandingPageAnimatedContainer } from '@/features/landing-page/landing-page-animated-container'
import { useEffect, useRef } from 'react'

export const FOOTER_HEIGHT = 500

export const LandingPageFooter = () => {
    const { handleCtaClick } = useLandingAuthContext()
    const { footerCtaOverride, footerCopyOverride } = useLandingPageContext()
    const currentYear = new Date().getFullYear()
    const previousMobileAccordionValuesRef = useRef<string[]>([])
    const accordionStartFrameRef = useRef<number | null>(null)
    const accordionResizeObserverRef = useRef<ResizeObserver | null>(null)
    const accordionStopTimeoutRef = useRef<number | null>(null)

    const aboutLinks = [
        { label: 'Pricing', href: PUBLIC_LANDING_ROUTES.PRICING },
        { label: 'Case Studies', href: PUBLIC_LANDING_ROUTES.CASE_STUDY },
        { label: 'News', href: PUBLIC_LANDING_ROUTES.NEWS },
        { label: 'Learn', href: PUBLIC_LANDING_ROUTES.LEARN },
        { label: 'Careers', href: 'https://superscale.notion.site/careers-jobs', external: true },
        { label: 'Affiliate Program', href: '/affiliate/partners' },
        { label: 'Status', href: 'https://status.superscale.ai/', external: true },
    ]

    const solutionLinks = ALL_SOLUTION_ITEMS.map((item) => ({
        label: item.title,
        href: item.href,
    }))

    const caseStudyLinks = [
        { label: 'All Case Studies', href: PUBLIC_LANDING_ROUTES.CASE_STUDY },
        { label: 'Sumup', href: '/case-study/sumup' },
        { label: 'Taxfix', href: '/case-study/taxfix' },
        { label: 'Advercy', href: '/case-study/advercy' },
        { label: 'Stromnow', href: '/case-study/stromnow' },
        { label: 'Twineo', href: '/case-study/twineo' },
        { label: 'Lila', href: '/case-study/lila' },
        { label: 'Ascend Bible', href: '/case-study/ascend-bible' },
        { label: 'Was Kann Ich Essen', href: '/case-study/was-kann-ich-essen' },
        { label: 'Marketbirds', href: '/case-study/marketbirds' },
    ]

    const compareLinks = [
        { label: 'Best AI marketing agents', href: '/compare/the-best-ai-marketing-agents' },
        { label: 'Best agentic platforms', href: '/compare/best-agentic-marketing-platforms' },
        { label: 'Best AI media buying', href: '/compare/best-ai-media-buying-tools-meta' },
        { label: 'vs Arcads', href: '/compare/superscale-vs-arcads' },
        { label: 'vs Creatify', href: '/compare/superscale-vs-creatify' },
        { label: 'vs HeyGen', href: '/compare/superscale-vs-heygen' },
        { label: 'vs Icon', href: '/compare/superscale-vs-icon' },
        { label: 'vs Laria', href: '/compare/superscale-vs-laria' },
        { label: 'vs MakeUGC', href: '/compare/superscale-vs-makeugc' },
        { label: 'vs Reelfarm', href: '/compare/superscale-vs-reelfarm' },
    ]

    const legalLinks = [
        { label: 'Imprint', href: PUBLIC_LANDING_ROUTES.IMPRINT },
        { label: 'Privacy Policy', href: PUBLIC_LANDING_ROUTES.PRIVACY_POLICY },
        { label: 'Terms & Conditions', href: PUBLIC_LANDING_ROUTES.TERMS_AND_CONDITIONS },
        { label: 'Affiliate Terms', href: '/affiliate/terms' },
        { label: 'Contact', href: 'mailto:support@superscale.ai', external: true },
    ]

    const socialLinks = [
        { label: 'X', href: 'https://x.com/superscale_ai', external: true },
        { label: 'LinkedIn', href: 'https://www.linkedin.com/company/superscale-ai', external: true },
        { label: 'Instagram', href: 'https://www.instagram.com/getsuperscaleai', external: true },
    ]

    // Top-row columns; Socials renders as its own section stacked below Legals (desktop) and as a
    // separate accordion item below Legals (mobile).
    const footerSections = [
        { title: 'About us', links: aboutLinks },
        { title: 'Solutions', links: solutionLinks },
        { title: 'Case Studies', links: caseStudyLinks },
        { title: 'Compare', links: compareLinks },
        { title: 'Legals', links: legalLinks },
    ]
    const socialsSection = { title: 'Socials', links: socialLinks }

    const stopMobileAccordionFollow = () => {
        if (accordionStartFrameRef.current !== null) {
            window.cancelAnimationFrame(accordionStartFrameRef.current)
            accordionStartFrameRef.current = null
        }

        if (accordionResizeObserverRef.current) {
            accordionResizeObserverRef.current.disconnect()
            accordionResizeObserverRef.current = null
        }

        if (accordionStopTimeoutRef.current !== null) {
            window.clearTimeout(accordionStopTimeoutRef.current)
            accordionStopTimeoutRef.current = null
        }
    }

    useEffect(() => {
        return () => {
            if (accordionStartFrameRef.current !== null) {
                window.cancelAnimationFrame(accordionStartFrameRef.current)
            }

            if (accordionResizeObserverRef.current) {
                accordionResizeObserverRef.current.disconnect()
            }

            if (accordionStopTimeoutRef.current !== null) {
                window.clearTimeout(accordionStopTimeoutRef.current)
            }
        }
    }, [])

    const handleMobileAccordionChange = (value: string[]) => {
        const previousValue = previousMobileAccordionValuesRef.current
        previousMobileAccordionValuesRef.current = value

        const newlyOpenedSection = value.find((sectionTitle) => !previousValue.includes(sectionTitle))
        if (!newlyOpenedSection || typeof window === 'undefined' || window.innerWidth >= 768) {
            stopMobileAccordionFollow()
            return
        }

        stopMobileAccordionFollow()

        const contentId = getFooterAccordionContentId(newlyOpenedSection)

        const startFollowing = () => {
            const content = document.getElementById(contentId)
            if (!content) return

            const keepContentInView = (behavior: ScrollBehavior = 'auto') => {
                const viewportBottom = window.innerHeight - 24
                const contentBottom = content.getBoundingClientRect().bottom

                if (contentBottom <= viewportBottom) return

                window.scrollBy({
                    top: contentBottom - viewportBottom,
                    left: 0,
                    behavior,
                })
            }

            keepContentInView()

            if (typeof ResizeObserver !== 'undefined') {
                const resizeObserver = new ResizeObserver(() => keepContentInView())
                resizeObserver.observe(content)
                accordionResizeObserverRef.current = resizeObserver
            }

            accordionStopTimeoutRef.current = window.setTimeout(() => {
                keepContentInView('smooth')
                stopMobileAccordionFollow()
            }, 450)
        }

        accordionStartFrameRef.current = window.requestAnimationFrame(() => {
            accordionStartFrameRef.current = window.requestAnimationFrame(() => {
                accordionStartFrameRef.current = null
                startFollowing()
            })
        })
    }

    return (
        <footer className="relative mx-3 mb-4 overflow-hidden rounded-[10px] md:mx-[18px] md:mb-6">
            {/* Background gradient image */}
            <Image
                src="/assets/gradients/footer_gradient.webp"
                alt=""
                fill
                className="object-cover pointer-events-none"
            />

            <div className="relative">
                {/* CTA Section */}
                <div className="flex flex-col items-center px-5 pt-16 pb-8 md:px-0 md:pt-32 md:pb-12">
                    <LandingPageAnimatedContainer
                        direction="up"
                        distance={60}
                        duration={0.6}
                        mobileDistance={30}
                        mobileDuration={0.5}
                    >
                        <div className="mx-auto flex w-full max-w-[593px] flex-col items-center text-center">
                            <LandingPageSectionHeader
                                as="h2"
                                title={footerCopyOverride?.title ?? 'You describe it.'}
                                titleFaded={footerCopyOverride ? footerCopyOverride.titleFaded : 'Your agent delivers it.'}
                                subtitle={
                                    footerCopyOverride
                                        ? footerCopyOverride.subtitle
                                        : 'Free to start · No credit card · Your first ads in under 5 minutes'
                                }
                            />

                            {footerCtaOverride ? (
                                <Button
                                    variant="ds2-primary"
                                    size="ds2-lg"
                                    className="px-4"
                                    onClick={() => {
                                        if ('onClick' in footerCtaOverride) {
                                            footerCtaOverride.onClick()
                                        } else {
                                            window.open(footerCtaOverride.href, '_blank')
                                        }
                                    }}
                                >
                                    {footerCtaOverride.label}
                                </Button>
                            ) : (
                                <Button
                                    variant="ds2-primary"
                                    size="ds2-lg"
                                    onClick={handleCtaClick}
                                    data-cta="get_started"
                                    data-section="footer"
                                    className="px-4"
                                >
                                    Get started free
                                </Button>
                            )}
                        </div>
                    </LandingPageAnimatedContainer>
                </div>

                {/* Footer content */}
                <div className="mx-auto flex w-full max-w-[1408px] flex-col gap-6 px-5 pt-6 pb-0 md:gap-8 lg:px-12 md:pt-[34px]">
                    <div className="flex w-full flex-col gap-6 md:gap-8">
                        <div className="w-[38px] h-[38px]">
                            <Logo className="w-full h-full" />
                        </div>

                        <Accordion
                            type="multiple"
                            onValueChange={handleMobileAccordionChange}
                            className="border-t border-[#161514]/10 md:hidden"
                        >
                            {[...footerSections, socialsSection].map((section) => (
                                <AccordionItem
                                    key={section.title}
                                    value={section.title}
                                    className="border-[#161514]/10"
                                >
                                    <AccordionTrigger className="py-3.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#161514]/56 hover:no-underline">
                                        {section.title}
                                    </AccordionTrigger>
                                    <AccordionContent id={getFooterAccordionContentId(section.title)} className="pb-5">
                                        <FooterLinksList
                                            links={section.links}
                                            className="gap-3 border-l border-[#161514]/10 pl-4"
                                            linkClassName="text-[14px] leading-[1.45] text-[#161514]/84"
                                        />
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>

                        <div className="hidden w-full md:grid md:grid-cols-5 md:gap-x-16">
                            {/* First four sections render as-is; Legals + Socials share the 5th column
                                stacked vertically so Socials sits directly under "Contact" instead of
                                being pushed down to clear the tallest top-row column (Solutions). */}
                            {footerSections.slice(0, -1).map((section) => (
                                <FooterLinkColumn key={section.title} title={section.title} links={section.links} />
                            ))}
                            <div className="flex flex-col gap-8">
                                <FooterLinkColumn
                                    title={footerSections[footerSections.length - 1].title}
                                    links={footerSections[footerSections.length - 1].links}
                                />
                                <FooterLinkColumn title={socialsSection.title} links={socialsSection.links} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="mx-auto flex w-full max-w-[1408px] items-center justify-between px-5 py-6 lg:px-12 md:py-5">
                    <p className="ds-caption text-[#191817]/50">&copy; {currentYear} Superscale</p>
                </div>
            </div>
        </footer>
    )
}

function getFooterAccordionContentId(title: string) {
    return `footer-accordion-content-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
}

type FooterLink = { label: string; href: string; external?: boolean }

function FooterLinkColumn({ title, links }: { title: string; links: FooterLink[] }) {
    return (
        <div className="flex flex-col gap-4">
            <h3 className="ds-caption text-[#161514]/72 opacity-80 uppercase tracking-[0.08em]">{title}</h3>
            <FooterLinksList links={links} />
        </div>
    )
}

function FooterLinksList({
    links,
    className,
    linkClassName,
}: {
    links: FooterLink[]
    className?: string
    linkClassName?: string
}) {
    return (
        <ul className={cn('flex flex-col gap-2', className)}>
            {links.map((link, i) => (
                <li key={`${link.href}-${i}`}>
                    {link.external ? (
                        <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                                'ds-caption text-[#161514] transition-colors hover:text-[#161514]/70',
                                linkClassName
                            )}
                        >
                            {link.label}
                        </a>
                    ) : (
                        <Link
                            href={link.href}
                            prefetch={false}
                            className={cn(
                                'ds-caption text-[#161514] transition-colors hover:text-[#161514]/70',
                                linkClassName
                            )}
                        >
                            {link.label}
                        </Link>
                    )}
                </li>
            ))}
        </ul>
    )
}
